import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRootStore } from "store";
import queryKeys from "queries/keys";
import { useQueryKeySpecificity } from "hooks/useQueryKeyAuth";
import { AccountMember, AccountMemberAttrs } from "models/Account";
import { updateMemberAttrs } from "services/accounts";

/**
 * @description
 * Mutation to update a an account member's attributes
 *
 * @param successCallback - function to run on success
 * @param errorCallback - function to run on error
 * @returns mutation object
 */

interface UpdateMemberAttrArgs {
  userId: string;
  attributes: Partial<AccountMemberAttrs>;
  prevState: AccountMember[]; // previous state of account members before the update
}

export const useUpdateMemberAttrs = (
  onSuccess?: Function,
  onError?: Function
) => {
  const {
    authStore,
    accountsStore: { currentAccountId },
    uiStore: { setApiErrorToast },
  } = useRootStore();
  const specificity = useQueryKeySpecificity({ context: currentAccountId });
  const queryClient = useQueryClient();
  const queryKey = queryKeys.accounts.collaborators(specificity).queryKey;

  return useMutation({
    mutationFn: (args: UpdateMemberAttrArgs) => {
      const { attributes, userId } = args;

      const member: AccountMember | undefined = queryClient
        .getQueryData<AccountMember[]>(queryKey)
        ?.find((member) => member?.user?.id === userId);

      if (!member) {
        throw new Error("Member not found");
      }

      const updatedMember = { ...member };
      const newAttributes: AccountMemberAttrs = {
        ...(member.attributes || {}),
        ...attributes,
      };

      updatedMember.attributes = newAttributes;

      queryClient.setQueryData(
        queryKey,
        (members: AccountMember[] | undefined) => {
          return members?.map((m) => {
            if (m.user.id === userId) {
              return updatedMember;
            }
            return m;
          });
        }
      );

      return currentAccountId
        ? updateMemberAttrs(authStore, currentAccountId, userId, newAttributes)
        : Promise.reject("No current account id");
    },
    onSuccess: (res: AccountMember) => onSuccess && onSuccess(res),
    onError: (err: any, vars: UpdateMemberAttrArgs) => {
      queryClient.setQueryData(queryKey, vars.prevState);
      setApiErrorToast(err, "Unable to update account member attributes");
      onError && onError(err);
    },
  });
};
